import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useCallback } from 'use-memo-one';
import { useFormatLocale } from '../../../locale/formatting/useFormatLocale';
import { isThaiLocale } from '../../../locale/localeUtils';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { postfixTestId } from '../../../util/testId';
import { Flow } from '../../Layouts/Flow';
import { Select } from '../../Select';
import { withLabels } from '../../withLabels';
import { convertGregorianYearToThaiYear, convertThaiYearToGregorianYear, getMonthAndYearByDate, getMonthsForLocale, getYearOrder } from '../DateUtil';
import { YearInput } from './YearInput';
import '../DatePicker.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SelectWithLabels = withLabels(Select);
export const MonthPicker = /*#__PURE__*/forwardRef(function MonthPicker(props, ref) {
  const localizeMonthPicker = useNCTextLocalizer('DatePicker');
  const locale = useFormatLocale();
  const months = useMemo(() => getMonthsForLocale(locale), [locale]);
  const yearOrder = useMemo(() => getYearOrder(locale), [locale]);
  const [isMonthValid, setIsMonthValid] = useState(true);
  const minMaxMonthErrorMsg = localizeMonthPicker('minmax.month.error.msg', {
    min: getMonthAndYearByDate(locale, props.minDate ?? new Date(1582, 0, 1)),
    max: getMonthAndYearByDate(locale, props.maxDate ?? new Date(9999, 11, 31))
  });
  const validateMonthValue = useCallback(value => {
    const date = new Date(props.value.getFullYear(), value, 5);
    date.setFullYear(props.value.getFullYear()); // if year is single digit, i.e., 1 or 0001, new Date will set year as 1901 instead of 0001
    if (props.value.getFullYear().toString().length >= 4 && props.minDate && date.getTime() < props.minDate.getTime() && props.value.getFullYear() >= props.minDate.getFullYear() || props.maxDate && date.getTime() > props.maxDate.getTime() && props.value.getFullYear() <= props.maxDate.getFullYear()) setIsMonthValid(false);else setIsMonthValid(true);
  }, [props.maxDate, props.minDate, props.value]);
  const refSelect = useRef(null);
  const refInput = useRef(null);
  useImperativeHandle(ref, () => ({
    isValid: () => {
      if (!refInput.current?.isValid() || !isMonthValid) return false;
      return true;
    },
    focus: () => !isMonthValid ? refSelect.current?.focus() : refInput.current?.focus()
  }));
  const [isValid, setIsValid] = useState(true);
  useEffect(() => {
    setTimeout(() => {
      refSelect.current?.focus();
    });
    refSelect.current?.setAttribute('data-autofocus', 'true');
  }, []);
  useEffect(() => {
    // Need to briefly delay validation when opening the dialog so the error message is displayed in
    // the proper location if invalid.  This small delay ensures the dialog is opened and controls are visible.
    setTimeout(() => {
      validateMonthValue(props.value.getMonth());
      setIsValid(refInput.current?.isValid() ?? true);
    }, 0);
  }, [props.value, validateMonthValue]);
  const yearElement = /*#__PURE__*/_jsx(YearInput, {
    testId: postfixTestId(props.testId, 'yearInput'),
    ref: refInput,
    value: isThaiLocale(locale) ? convertGregorianYearToThaiYear(props.value.getFullYear()) : props.value.getFullYear(),
    onChange: nextYear => {
      const nextValue = new Date(props.value);
      nextValue.setFullYear(isThaiLocale(locale) ? convertThaiYearToGregorianYear(nextYear) : nextYear);
      const isValidDate = nextValue instanceof Date && !isNaN(nextValue.getTime());
      if (!isValidDate) {
        setIsValid(false);
        return;
      }
      props.onChange(nextValue);
    },
    minDate: props.minDate,
    maxDate: props.maxDate
  });
  return /*#__PURE__*/_jsxs(Flow, {
    spacing: "s",
    orientation: "horizontal",
    children: [yearOrder === 'before' && yearElement, /*#__PURE__*/_jsx(SelectWithLabels, {
      testId: postfixTestId(props.testId, 'monthSelect'),
      popoverClassName: props.selectClassName,
      selectBoxRef: refSelect,
      selectedIndex: props.value.getMonth(),
      values: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(monthIndex => ({
        text: months[monthIndex]
      })),
      onSelected: selectedIndex => {
        const nextValue = new Date(props.value);
        nextValue.setDate(1);
        nextValue.setMonth(selectedIndex ?? 0);
        props.onChange(nextValue);
        validateMonthValue(nextValue.getMonth());
      },
      message: !isMonthValid ? minMaxMonthErrorMsg : undefined,
      status: isValid && !isMonthValid ? 'alert' : undefined,
      disabled: !isValid,
      label: localizeMonthPicker('month.label'),
      labelPosition: "top",
      fieldMinWidth: "10rem",
      wrapLabel: false
    }), yearOrder === 'after' && yearElement]
  });
});
//# sourceMappingURL=MonthPicker.js.map